import { Component, JSX } from 'react';
import { toReadableBytes } from '../../../utils/file';
import { previewKindFor, PreviewKind } from '../../../utils/preview-kind';
import { formatOptionEcho } from '../../../utils/option-echo';
import { chainToTool } from '../../../services/chain-to-tool.service';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { container } from '../../../context/module';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { suggestionsFor } from '../../../utils/next-tool';
import { canPreviewOutput, CapabilityResult } from '../../../utils/media-capability';

import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { EnvironmentService } from '../../../services/environment.service';
import { EnforcementErrorService } from '../../../services/enforcement-error.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { ProcessCompleteCheckService } from '../../../services/process-complete-check.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { GAContants } from '../../../types/ga.types';
import { OptionalChildrenProps } from '../../../types/react.types';
import { RouteProps } from '../../../types/route-types';
import {
    ProcessStageSubStateTypes,
    ProcessStageTypes,
    ProcessTypes,
    StatusTypes,
    WebsocketProcessTypes,
    WebsocketResponse,
} from '../../../types/types';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { ConvertOptionsV2Utils } from '../../../utils/convert-options-v2-utils';
import { PSDate, PSRedableDuration } from '../../../utils/date.utils';
import { getExtension, replaceExtension } from '../../../utils/file';
import { get, isNaN, isNil, isNumber, isString, toInteger } from '../../../utils/lodash-min';
import { SafeExecUtils } from '../../../utils/safe-exec.utils';
import { StringUtils } from '../../../utils/string-utils';
import { FileUploadReEditModalComponent } from '../file-upload-reedit/file-upload-reedit-modal.component';
import { MediaPreviewModalComponent } from '../media-preview/media-preview-modal.component';
import Modal from '../modal/modal.component';
import ProgressComponent from '../progress/progress.component';
import { RippleLoaderComponent } from '../ripple-loader/ripple-loader.component';
import { SingleFileDownloadModalComponent } from '../single-fle-download/single-file-download.component';
import Tooltip from '../tooltip/tooltip.component';
import styles from './file-list-item.component.module.scss';

declare var gapi;
declare var Dropbox;

interface FileListItemComponentProps extends RouteProps, OptionalChildrenProps {
    statusCheckButtonEnabled?: boolean;
    reeditButtonEnabled?: boolean;
    deleteButtonEnabled?: boolean;
    fileUpload?: FileUpload;
    hideFooterButtons?: boolean;
}
interface FileListItemComponentState {
    zipDownloadInProgress: boolean;
    linkCopied: boolean;
    creditOpen: boolean;
    creditFormat: 'html' | 'markdown' | 'plain';
    creditCopied: boolean;
}

export class FileListItemComponent extends Component<FileListItemComponentProps, FileListItemComponentState> {
    private readonly logger = Logger.getLogger('FileListItemComponent');
    readonly FILEUPLOAD_STATE_FAILED = StatusTypes.FAILED;

    fileProcessInfo: string;
    fileOutputInfo: string | JSX.Element;


    get fileUpload() {
        return this.props.fileUpload;
    }

    get hasZipFileInfo() {
        return this.props.fileUpload && this.props.fileUpload.zipResult;
    }

    get zipSupported() {
        return this.fileUpload && (this.fileUpload.processType === ProcessTypes.SPLIT ||
            (this.fileUpload.ws && this.fileUpload.ws.processType === WebsocketProcessTypes.CONVERT_ONE_TO_MANY));
    }

    get canRequestZipFile() {
        return this.zipSupported && !this.fileUpload.zipResult;
    }

    get isZipFileReady() {
        return this.hasZipFileInfo && this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED;
    }

    get hasZipFileData() {
        if (!this.hasZipFileInfo) {
            return 'No data';
        }
        if (this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
            return 'File zipped successfully';
        }
        return this.fileUpload.zipResult.data;
    }

    get statusCheckButtonEnabled() {
        return isNil(this.props.statusCheckButtonEnabled) || this.props.statusCheckButtonEnabled;
    }
    get reeditButtonEnabled() {
        return isNil(this.props.reeditButtonEnabled) || this.props.reeditButtonEnabled;
    }
    get deleteButtonEnabled() {
        return isNil(this.props.deleteButtonEnabled) || this.props.deleteButtonEnabled;
    }

    get hasMultipleFilesToDownload(): boolean {
        return this.fileUpload && this.fileUpload.result && Array.isArray(this.fileUpload.result.output);
    }

    @lazyInject(FileDownloadService) private fileDownloadService: FileDownloadService;
    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(MetricsService) private metricsService: MetricsService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(FileProcessService) private fileProcessService: FileProcessService;
    @lazyInject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService;
    @lazyInject(EnvironmentService) private envService: EnvironmentService;

    readonly state: Readonly<FileListItemComponentState> = { zipDownloadInProgress: false, linkCopied: false, creditOpen: false, creditFormat: 'html', creditCopied: false };

    /**
     * Suggestions that need a browser-decodable image, once the runtime check has answered.
     * Until then they are withheld: offering "Resize Image" for a TIFF sends the user to a
     * canvas editor that cannot open it and silently shows its landing page instead.
     */
    private editorSafe: boolean | null = null;

    /** Guards the async capability probe from updating a card the user already deleted. */
    private mounted = false;

    /** Slug being chained from THIS card, so only its own chip shows progress. */
    private chaining: string | null = null;
    /** Why the file did not travel, shown on this card rather than as a page-level toast. */
    private chainNotice: string | null = null;

    /**
     * Clear the busy flag when the page comes back.
     *
     * Chaining navigates away, so nothing ever cleared `chaining` on the success path -- and a
     * browser Back restores this page from the bfcache without remounting, so the flag survived.
     * The chip stayed on "Taking your file…" and `if (this.chaining) return` then swallowed
     * every later click on every chip. Reported as "none of those pills are clickable".
     */
    private resetChainState = () => {
        if (!this.mounted) return;
        if (this.chaining || this.chainNotice) {
            this.chaining = null;
            this.chainNotice = null;
            this.forceUpdate();
        }
    };

    getZipFileURL(fileInfo: FileUpload) {
        if (!fileInfo.result) {
        } else if (fileInfo.result.error === true) {
        } else {
            try {
                const tokens = fileInfo.result.zip.split('\\');
                return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
            } catch (e) {
                return;
            }
        }
    }

    componentDidMount() {
        this.mounted = true;
        const toType = this.props.fileUpload?.toType;
        if (toType) {
            canPreviewOutput(`${toType}`)
                .then(verdict => {
                    // 'unknown' counts as safe: the editor is the one that will find out, and
                    // withholding a useful destination on a maybe is the worse error.
                    this.applyEditorSafe(verdict !== 'no');
                })
                .catch(() => this.applyEditorSafe(true));
        }
        if (BrowserUtils.hasWindow()) {
            // `pageshow` fires on a bfcache restore, where componentDidMount does not run again.
            window.addEventListener('pageshow', this.resetChainState);
            window.addEventListener('visibilitychange', this.resetChainState);
        }
    }

    /** The capability probe resolves off-thread; a card removed before then must not update. */
    private applyEditorSafe(safe: boolean) {
        if (!this.mounted || safe === this.editorSafe) return;
        this.editorSafe = safe;
        this.forceUpdate();
    }

    componentWillUnmount() {
        this.mounted = false;
        if (BrowserUtils.hasWindow()) {
            window.removeEventListener('pageshow', this.resetChainState);
            window.removeEventListener('visibilitychange', this.resetChainState);
        }
    }

    async reedit(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        const initialState = {
            fileUpload,
        };
        Modal.createModal(FileUploadReEditModalComponent, initialState);
    }

    private async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
            }
        }, 'Please wait while we initialize file upload service...');
    }

    hasDirectDownloadLink(fileUpload: FileUpload): boolean {
        if (!this.isFileReady(fileUpload) || fileUpload.result.error === true) {
            return false;
        }

        if (Array.isArray(fileUpload.result.output)) {
            return false;
        }
        return true;
    }

    getRangeSupportedDirectDownloadLink(fileUpload: FileUpload, preview = false): string {
        if (!this.hasDirectDownloadLink(fileUpload)) {
            return null;
        }
        if (Array.isArray(fileUpload.result.output)) {
            return null;
        }

        const tokens = fileUpload.result.output.split('\\');
        const link = this.fileDownloadService.getRangeSupportedDirectDownloadLink(fileUpload, tokens[tokens.length - 1], preview);
        setTimeout(() => {
            gapi.savetodrive.go(`id${this.props.fileUpload.id}`);
        }, 100);
        return link;
    }

    async onDirectDownloadBtnHandler(fileInfo: FileUpload) {
        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                const link = this.fileDownloadService.getDirectDownloadLink(fileInfo, tokens[tokens.length - 1]);
                this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left; word-break: break-all;">
                        <div>
                            Filename: 
                            <div class="alert alert-info">${fileInfo.name}</div>
                        </div>
                        <div>
                            Link:
                            <div class="alert alert-info" style="">${link}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
            }
        }
    }


    onDownloadBtnHandler(fileInfo: FileUpload) {
        // Session-context download signal (sweep P2): the server flag says WHAT was
        // downloaded; this says when/where in the session it happened.
        const metrics = container.get(MetricsService);
        metrics.downloadedThisSession = true;
        metrics.emit(MetricEventName.FILE_DOWNLOAD, {
            workflowId: String((fileInfo as any).workflowId ?? (fileInfo as any).uuid ?? ''),
            page: typeof location !== 'undefined' ? location.pathname : '',
        });
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
            }
        }
    }

    async onZipDownloadBtnHandler(fileInfo: FileUpload) {
        if (!fileInfo.result) {
            this.uiContext.warnToast('Processing File',
                'We are processing your file. Please wait...', false, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast('Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`, true, true);
        } else {
            await this.initializeScriptsAndWebsockets();
            if (this.isZipFileReady) {
                this.openZippedFileDownloadWindow(fileInfo);
            } else if (!this.canRequestZipFile) {
                this.uiContext.warnToast('Processing File',
                    'We are processing your file. Please wait...', false, true);
                return;
            } else {
                this.spinner.runWithLoader(async () => {
                    this.setState({ zipDownloadInProgress: true });
                    await this.fileProcessService.createZip(fileInfo).then(() => {
                        this.logger.trace1('success');
                        this.openZippedFileDownloadWindow(fileInfo);
                        this.setState({ zipDownloadInProgress: false });
                    }).catch((err) => {
                        this.logger.error(err);
                        this.setState({ zipDownloadInProgress: false });
                    });
                    console.log('Zip completed');
                }, 'Zipping files for downloading..');
            }

        }
    }

    private openZippedFileDownloadWindow(fileInfo) {
        const tokens = fileInfo.result.zip.split('\\');
        const downloadWindowOpened = this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
        if (!downloadWindowOpened) {
            this.uiContext.warnToast('Pop-up blocked',
                'If file upload failed. Please make sure your browser not blocking pop-ups...', false, true);
        }
        return;
    }

    getDownloadURL(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
        } else if (fileInfo.result.error === true) {
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                return 'modal';
            } else {
                try {
                    const tokens = fileInfo.result.output.split('\\');
                    return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
                } catch (e) {
                    this.logger.error({ message: `Failed to decode result.output`, error: e, data: FileUpload.toLoggableObject(fileInfo) });
                    return;
                }
            }
        }
    }

    getOutputInfo(fileUpload: FileUpload): JSX.Element | string {
        if (!this.fileOutputInfo) {
            this.fileOutputInfo = this._getOutputInfo(fileUpload);
        }
        return this.fileOutputInfo;
    }

    private _getOutputInfo(fileUpload: FileUpload) {
        if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const key = Object.keys(fileUpload.result.sizes.outputs);
            const outputSize = parseFloat(fileUpload.result.sizes.outputs[key[0]]);
            const ratio = parseFloat(1 * (100 * (1 - outputSize / fileUpload.size)) + '').toFixed(2);
            const ratio2 = parseFloat(1 * (100 * (outputSize / fileUpload.size)) + '').toFixed(2);

            return <div>
                {/* <div>Original size: {this.getFileSize(fileUpload.size)}</div> */}
                <div>New size:  {this.getFileSizeInfo(fileUpload)} ({ratio2}% of the original {this.getFileSize(fileUpload.size)})</div>
            </div>;
        }
        return `Output Size: ${this.getFileSizeInfo(fileUpload)}`;
    }

    private getFileSizeInfo(fileUpload: FileUpload) {
        if (fileUpload.processType !== ProcessTypes.SPLIT) {
            try {
                const key = Object.keys(fileUpload.result.sizes.outputs);
                return this.getFileSize(fileUpload.result.sizes.outputs[key[0]]);
            } catch (e) {
                this.logger.error({ message: e.message, error: e });
            }
        }
        return '';
    }

    private getFileSize(size) {
        // Decimal (SI), matching the tool-page file card and the backend's size targets.
        // This used to be filesize(size, { base: 2, standard: 'jedec' }) -- binary maths
        // wearing decimal labels -- so one 590,213-byte file read "590.21 KB" on the tool
        // page and "576.38 KB" here. On a site whose whole job is file size, the number
        // changing between screens reads as a bug in the conversion.
        return toReadableBytes(size, 'quantity');
    }

    isSingleFile(outputs: any) {
        return Object.keys(outputs).length === 1;
    }

    decodeDataType(fileUpload: FileUpload, dataType: string, data: string) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (dataType === 'enforcement_error') {
            let parsed: Record<string, unknown> = {};
            try {
                parsed = JSON.parse(data);
            } catch { /* fall through to fallback copy */ }
            // Surface in the global modal as well so the user gets the same
            // structured rejection UI regardless of which code path (pre-upload
            // resolve 429 vs post-upload WS) triggered the block.
            try {
                this.enforcementErrorService?.show(
                    EnforcementErrorService.fromBody(parsed as never, 429),
                );
            } catch { /* best-effort — never block the inline message */ }
            return (parsed as { userMessage?: string }).userMessage
                || 'Usage limit reached. Please try again later.';
        }
        if (dataType === 'notification') {
            if (wsData.processStage == ProcessStageTypes.UPLOAD) {
                // Server-to-server transfer progress. CONTRACT with ironman
                // downloadUploadedFilesToLocalDisk: `data` is the raw cumulative byte
                // count as a decimal string. Anything else (legacy "Retrieved 12.34 MB",
                // "File retrieval started/completed.") must fall back to plain copy —
                // lodash toInteger() turned those into 0, so the card showed a frozen
                // "0 Bytes/<total>" for the entire transfer.
                const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                const dataVal = /^\d+$/.test(String(data).trim()) ? toInteger(data) : NaN;
                const totalOk = totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize);
                if (wsData.processStageSubState == ProcessStageSubStateTypes.COMPLETED) {
                    if (totalOk && !isNaN(dataVal) && dataVal > 0) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                    return 'File sent to worker';
                } else if (wsData.processStageSubState == ProcessStageSubStateTypes.PARTIAL) {
                    if (totalOk && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                }
                return 'Sending file to worker';
            } else if (wsData.processStage == ProcessStageTypes.PREPROCESS || wsData.processStage == ProcessStageTypes.PROCESSING) {
                return data;
            }
            if (wsData.processStage == ProcessStageTypes.COMPLETED && data.toLocaleLowerCase() === 'completed') {
                const startTimeInMilli = PSDate.from(fileUpload.uploadStartTime).getTime();
                if (fileUpload.processEndTime) {
                    const endTimeInMilli = PSDate.from(fileUpload.processEndTime).getTime();
                    const prettyTimeMs = new PSRedableDuration(PSDate.from(fileUpload.uploadStartTime))
                        .fromDate(PSDate.from(fileUpload.processEndTime));
                    return `${data}: Took ${prettyTimeMs}`;
                }
                const currentTimeInMilli = PSDate.from().toDate().getTime();
                const prettyTime = new PSRedableDuration(PSDate.from().subtract(currentTimeInMilli - startTimeInMilli, 'ms'))
                    .fromNow();
                return `${data} ${prettyTime}`;
            }
            return data || 'Server Processing File';
        } else if (dataType === 'ratio') {
            return 'Server Processing File';
        } else if (dataType === 'percentage') {
            const message = get(fileUpload, 'ws.readableProcessStage') || 'Server Processing File';
            return message;
        } else {
            return 'Waiting for server to reply';
        }
    }

    getProcessInfo(fileUpload: FileUpload): string {
        if (!this.fileProcessInfo) {
            this.fileProcessInfo = this._getProcessInfo(fileUpload);
        }
        return this.fileProcessInfo;
    }

    private _getProcessInfo(fileUpload: FileUpload): string {
        const inputExtension = getExtension(fileUpload.name);
        if (fileUpload.processType === ProcessTypes.SPLIT) {
            const splitOptions = ConvertOptionsUtils.getPropertyValue(fileUpload, 'split');
            if (splitOptions && splitOptions.formValue === 'page_range') {
                return `Extracted PDF ${splitOptions.formChildren[0].formValue} to ${splitOptions.formChildren[1].formValue}`;
            }
            if (fileUpload.fromType.includes('archive')) {
                return `Extracted to individual files`;
            }
            return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}ted PDF to individual pages`;
        } else if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const outputQualityValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQuality')
                || ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQualityAndSize');

            const crfValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'crf')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'crf');
            const presetValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'preset')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'preset');
            const qminValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmin')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmin');
            const qmaxValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmax')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmax');

            const minrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            const maxrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            if (outputQualityValue && isString(outputQualityValue)) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with quality
                            ${outputQualityValue === '-1' ? 'unchanged' : outputQualityValue + '%'}`;
            } else if (crfValue && presetValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                             ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and Preset ${presetValue}`;
            } else if (crfValue && qminValue && qmaxValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                            Quality ${qminValue}-${qmaxValue}%`;
            } else if (crfValue && minrate || minrate) {
                if (minrate &&
                    minrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate) - ${maxrate} (maxrate)`;
                } else if (maxrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${maxrate} (maxrate)`;
                } else {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate)`;
                }

            } else {
                const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))} ${inputExtension.toLocaleUpperCase()}`;
                return processInfo;
            }
        } else if (fileUpload.processType === ProcessTypes.MERGE) {
            return `${StringUtils.capitalizeFirstLetter(
                this.getProcessTypePastTense(fileUpload.processType))} ${fileUpload.fromType[0].toUpperCase()} files to ${fileUpload.toType.toUpperCase()}`;
        }
        else if (fileUpload.processType === ProcessTypes.EXTRACT) {
            return `${StringUtils.capitalizeFirstLetter(`Extracted ${fileUpload.extension[0].toUpperCase()}`)}`;
        }
        else if (fileUpload.processType === ProcessTypes.ARCHIVE) {
            return `${StringUtils.capitalizeFirstLetter(`Archived files to ${fileUpload.toType.toUpperCase()}`)}`;
        }

        const toExtensionOverride = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtensionOverride');
        let toExtension;
        if (toExtensionOverride && toExtensionOverride !== 'SAME_AS_SOURCE') {
            toExtension = toExtensionOverride;
        } else if (toExtensionOverride === 'SAME_AS_SOURCE') {
            toExtension = inputExtension; // use input file's extension
        } else {
            toExtension = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtension');
        }
        let extension = toExtension ? getExtension(toExtension) : undefined;
        try {
            if (extension === 'original') {
                extension = getExtension(fileUpload.originalInputFiles[0].originalName);
            }
        } catch (e) { }
        const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
             ${inputExtension.toLocaleUpperCase()} to ${extension?.toLocaleUpperCase()}`;
        return processInfo;
    }

    getProcessTypePastTense(pType: string) {
        if (/resize$/i.test(pType)) {
            return 'resized';
        } else if (/merge$/i.test(pType)) {
            return 'merged';
        } else if (/extract$/i.test(pType)) {
            return 'extracted';
        } else if (/convert$/i.test(pType)) {
            return 'converted';
        } else if (/rotate$/i.test(pType)) {
            return 'rotated';
        } else if (/trim$/i.test(pType)) {
            return 'trimmed';
        } else if (/crop$/i.test(pType)) {
            return 'cropped';
        } else if (/compress$/i.test(pType)) {
            return 'compressed';
        } else {
            return pType;
        }
    }

    decodeData(fileUpload: FileUpload) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (wsData.dataType === 'percentage') {
            if (wsData.processStage === 'processing') {
                if (wsData.data.match(/^completed$/i)) {
                    return '100';
                }
                const val = parseFloat(wsData.data).toFixed(2);
                return val;
            }
        } else if (wsData.dataType === 'notification') {
            if (wsData.processStage === 'upload') {
                if (wsData.processStageSubState.match(/^completed$/i)) {
                    return '100';
                }
                const val = Number(100 * parseFloat(wsData.data) / fileUpload.size).toFixed(2);
                return val;
            }
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else {
            return 'Waiting for server to reply';
        }
    }

    isFileReady(fileInfo: FileUpload) {
        if (!fileInfo.result || Object.keys(fileInfo.result).length === 0) {
            return false;
        }
        return true;
    }

    checkStatus(fileInfo: FileUpload) {
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        ).then(data => this.logger.trace7(data))
            .catch(err => this.logger.error({ error: err, message: `Error while checking status ${err.message}` }));
    }

    async checkStatusAndShowMessage(fileInfo: FileUpload) {
        try {
            await this.initializeScriptsAndWebsockets();
            this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
                fileInfo.endpointData.processorMetadataList[0].processDomain,
                fileInfo.id,
            ).then(stateRespose => {
                if (stateRespose.state === 'success') {
                    this.uiContext.successToast(
                        'Process Success',
                        'Process successfully completed. Please refresh page.',
                    );
                } else if (stateRespose.state === 'error') {
                    this.uiContext.errorToast(
                        'Error',
                        'Process failed.',
                        false,
                        false,
                    );
                } else {
                    this.uiContext.warnToast(
                        'Unknown',
                        'It\'s looks like server still processing file.',
                        false,
                        false,
                    );
                }
            }).catch(err => {
                this.uiContext.handleError(err);
            }).finally(() => {
                this.forceUpdate();
            });
        } catch (e) {
            this.uiContext.handleError(e);
        }
    }

    getFileDownloadBtnText(fileUpload: FileUpload) {
        const numberOfOutputFiles = get(fileUpload, 'result.outputFiles', []).length;
        if (numberOfOutputFiles > 1) {
            return `Show Downloads (${numberOfOutputFiles} files)`
        }
        return 'Download';
    }

    getOutputFileNameIfAvailable(fileUpload: FileUpload) {
        let filename = fileUpload.name;
        const override = fileUpload.options?.toExtensionOverride;
        let toExtension;
        if (override && override !== 'SAME_AS_SOURCE') {
            toExtension = override;
        } else if (override === 'SAME_AS_SOURCE') {
            toExtension = getExtension(fileUpload.name);
        } else {
            toExtension = fileUpload.options?.toExtension ?? getExtension(fileUpload.originalInputFiles[0].originalName);
            if (toExtension === 'SAME_AS_SOURCE') {
                toExtension = getExtension(fileUpload.name);
            }
        }
        filename = replaceExtension(fileUpload.name, toExtension);
        return filename;
    }

    async removeFile(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Delete File',
            'Are you sure you want to delete?',
            async () => {
                await this.fileManagerService.removeFile(fileUpload);
            },
        );
    }

    /**
     * Show the user their result.
     *
     * Two sources are always in play: the real file, and a server-rendered still of it. The
     * capability check only decides which to *start* with — no table is right on every browser,
     * OS and codec combination, so the modal attempts the real file and falls back to the still
     * on a genuine decode failure. Being wrong optimistically costs one request; being wrong
     * pessimistically costs the real preview. Both recover, and neither dead-ends the user,
     * which the old "download the file to view it" toast did on the exact control they clicked
     * to find out whether the conversion worked.
     */
    openPreview(fileUpload: FileUpload) {
        const kind = previewKindFor(fileUpload.toType);
        if (!kind) return;
        container.get(MetricsService).emit(MetricEventName.PREVIEW_OPENED, {
            workflowId: String((fileUpload as any).workflowId ?? (fileUpload as any).uuid ?? ''),
            kind,
        });
        if (!this.isFileReady(fileUpload)) {
            this.uiContext.errorToast('Not ready yet', 'This file is still converting.', false, false);
            return;
        }
        canPreviewOutput(fileUpload.toType, (fileUpload as any).result?.videoCodec)
            .then(verdict => this.showPreview(fileUpload, kind, verdict))
            // An unreachable capability check is not a reason to refuse: attempt it.
            .catch(() => this.showPreview(fileUpload, kind, 'unknown'));
    }

    private showPreview(fileUpload: FileUpload, kind: PreviewKind, verdict: CapabilityResult) {
        // Audio has no server-rendered still to fall back to (the thumb endpoint 404s),
        // so never offer one: a failed audio preview should say so, not chase a poster.
        const poster = kind === 'audio' ? null : this.outputPosterUrl(fileUpload);
        const format = (fileUpload.toType || 'this format').toUpperCase();
        const fallbackNotice = `Your browser can't open ${format} directly, so this is a still `
            + 'image of the converted result. Download the file to use it.';

        const realUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);

        // 'no' means this browser definitely cannot decode it, so go straight to the poster
        // rather than spending the user's bandwidth on bytes that will not render. Download
        // still points at the real file.
        if (verdict === 'no' && poster) {
            MediaPreviewModalComponent.show({
                url: poster,
                downloadUrl: realUrl,
                mediaType: 'image' as const,
                filename: fileUpload.name,
                notice: fallbackNotice,
            });
            return;
        }

        // 'yes' and 'unknown' both attempt the real file; the modal swaps to the poster if it
        // genuinely fails to render, which is what makes a wrong capability answer survivable.
        MediaPreviewModalComponent.show({
            url: realUrl,
            downloadUrl: realUrl,
            mediaType: kind === 'video' ? 'video' as const
                : kind === 'audio' ? 'audio' as const
                    : fileUpload.toType === 'pdf' ? 'pdf' as const
                        : 'image' as const,
            filename: fileUpload.name,
            fallbackUrl: poster || undefined,
            fallbackNotice: poster ? fallbackNotice : undefined,
        });
    }

    /** Server-rendered still of the output, or null when there is nothing to ask for. */
    private outputPosterUrl(fileUpload: FileUpload): string | null {
        const output = fileUpload?.result?.output;
        if (!output || Array.isArray(output)) return null;
        const tokens = output.split('\\');
        return this.fileDownloadService.getOutputThumbnailLink(fileUpload, tokens[tokens.length - 1]);
    }

    async saveToDropbox(filename: string, downloadLink: string) {
        const instant = await this.spinner.show('Uploading to Dropbox');
        var options = {
            files: [
                // You can specify up to 100 files.
                { 'url': downloadLink, 'filename': filename },
                // ...
            ],

            // Success is called once all files have been successfully added to the user's
            // Dropbox, although they may not have synced to the user's devices yet.
            success: () => {
                // Indicate to the user that the files have been saved.
                this.spinner.hide(instant);
            },

            // Progress is called periodically to update the application on the progress
            // of the user's downloads. The value passed to this callback is a float
            // between 0 and 1. The progress callback is guaranteed to be called at least
            // once with the value 1.
            progress: (progress) => {
                this.spinner.updateMessage(instant, `Uploading to Dropbox. ${progress * 100} completed.`);
                if (progress === 1) {
                    this.spinner.hide(instant);
                }
            },

            // Cancel is called if the user presses the Cancel button or closes the Saver.
            cancel: () => {
                this.spinner.hide(instant);
            },

            // Error is called in the event of an unexpected response from the server
            // hosting the files, such as not being able to find a file. This callback is
            // also called if there is an error on Dropbox or if the user is over quota.
            error: (errorMessage) => {
                this.spinner.hide(instant);
                this.uiContext.showErrorDialog({ title: 'Error', text: errorMessage });
            }
        };
        Dropbox.save(downloadLink, filename, options);
    }

    getFooterButtons(fileUpload) {
        return <div className={styles['footer-buttons']} key={'file-list-item-' + fileUpload.id} data-jkey={`file-list-item-footer`}>
            <div className={styles['action-btns']}>
                {
                    this.zipSupported && <button
                        disabled={this.state.zipDownloadInProgress || !this.isFileReady(fileUpload) || null}
                        data-url={this.getZipFileURL(fileUpload)}
                        className={`${styles['btn']} ${!fileUpload.result || (fileUpload.result && !fileUpload.result.zip) ? 'hide' : ''}`}
                        onClick={() => this.onZipDownloadBtnHandler(fileUpload)}
                        data-jkey={`download-btn-zip`}>
                        Download All (Zip)
                    </button>
                }

                <button data-url={this.getDownloadURL(fileUpload)}
                    className={`${styles['btn']} ${styles['btn-primary']}`}
                    onClick={() => this.onDownloadBtnHandler(fileUpload)}
                    disabled={!this.isFileReady(fileUpload)}
                    data-jkey={`download-btn`}>
                    {this.getFileDownloadBtnText(fileUpload)}
                </button>

                {
                    /* Labelled, and next to Download rather than buried at the end of an
                       unlabelled icon row. On lossy image/GIF output a quarter of users
                       download nothing (see RETENTION_FIXES.md T2.4) -- they cannot judge
                       quality they were never shown. */
                    previewKindFor(fileUpload.toType) && <button
                        className={styles['btn']}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.openPreview(fileUpload)}
                        data-jkey={`preview-btn`}>
                        <img src="/icons/svg/play.svg" alt="" aria-hidden="true" /> Preview
                    </button>
                }

                {
                    false && this.hasDirectDownloadLink(fileUpload) && <div style={{ display: 'inline-block' }}>
                        <div className="g-savetodrive"
                            data-src={this.getRangeSupportedDirectDownloadLink(fileUpload)}
                            data-filename={fileUpload.originalInputFiles[0].originalName}
                            data-sitename="www.xconvert.com"
                            data-jkey={`download-drive`}>
                        </div>
                    </div>
                }

                {
                    this.hasDirectDownloadLink(fileUpload) && <Tooltip text="Save To Dropbox">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            disabled={!this.isFileReady(fileUpload)}
                            onClick={() => this.saveToDropbox(fileUpload.originalInputFiles[0].originalName, this.getRangeSupportedDirectDownloadLink(fileUpload))} >
                            <img className={styles['dropdox-image']} src="/img/dropbox.png" alt=""
                                data-jkey={`download-dropbox`} />
                        </button>
                    </Tooltip>
                }

                <Tooltip text="Downloadable Link">
                    <button data-url={this.getDownloadURL(fileUpload)}
                        className={`${styles['btn']} ${styles['btn-icon']}`}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.onDirectDownloadBtnHandler(fileUpload)}
                        data-jkey={`file-list-item`}>
                        <img src="/icons/svg/link-45deg.svg" alt="" aria-hidden="true" />
                    </button>
                </Tooltip>

                {
                    this.statusCheckButtonEnabled && <Tooltip text="Check Status">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            onClick={() => this.checkStatusAndShowMessage(fileUpload)}
                            data-jkey={`check-status`}>
                            <img src="/icons/svg/question.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }
                {
                    this.reeditButtonEnabled && <Tooltip text="Re-edit">
                        <button className={styles['btn']}
                            onClick={() => this.reedit(fileUpload)}
                            data-jkey={`edit-btn`}>
                            <img src="/icons/svg/pencil-square.svg" alt="" aria-hidden="true" /> Re-edit
                        </button>
                    </Tooltip>
                }
                {
                    this.deleteButtonEnabled && <Tooltip text="Delete">
                        <button className={`${styles['btn']} ${styles['btn-danger']} ${styles['btn-icon']}`}
                            onClick={() => this.removeFile(fileUpload)}
                            data-jkey={`delete-btn`}>
                            <img src="/icons/svg/trash.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }

            </div>
            {this.getNextSteps(fileUpload)}
        </div>
    }

    /**
     * "What next?" for this specific file, on this specific card.
     *
     * It used to be one block at the bottom of /downloads derived from the newest result only.
     * With three finished files that is ambiguous -- which one does it act on? -- and measured on
     * QA at 412px it rendered at y=2,420 against a first card at y=501, so nobody reached it.
     * A continuation belongs beside the Download button for the file it continues.
     */
    /**
     * Has this file's conversion actually failed?
     *
     * `status === FILEUPLOAD_STATE_FAILED` only covers a failed UPLOAD, so it is false for a
     * file that uploaded fine and then failed to convert -- which is most failures. Anything
     * deciding whether to show success-shaped UI needs all three signals, not just that one.
     */
    hasFailed(fileUpload: FileUpload): boolean {
        return fileUpload?.status === this.FILEUPLOAD_STATE_FAILED
            || fileUpload?.result?.error === true
            || fileUpload?.ws?.processStageSubState === ProcessStageSubStateTypes.FAILED;
    }

    getNextSteps(fileUpload: FileUpload) {
        if (!this.isFileReady(fileUpload) || fileUpload?.result?.error === true) return null;
        const steps = suggestionsFor(fileUpload.toType, (fileUpload as any)?.pageSlug || '', 3)
            .filter(t => !t.requiresBrowserDecode || this.editorSafe === true)
            .slice(0, 2);
        if (!steps.length) return null;

        return <div className={styles['next-steps']} data-jkey="card-next-steps">
            <span className={styles['next-steps-label']}>Next:</span>
            {steps.map((t, i) => (
                <a key={t.slug} href={`/${t.slug}`}
                    className={styles['next-step-chip']}
                    aria-busy={this.chaining === t.slug}
                    onClick={(e) => this.chainClick(e, fileUpload, t.slug, i)}>
                    {this.chaining === t.slug ? 'Taking your file…' : t.label}
                </a>
            ))}
            {this.chainNotice && (
                <span role="status" className={styles['next-steps-notice']}>{this.chainNotice}</span>
            )}
        </div>;
    }

    /**
     * Carry this card's output to the next tool. Modified clicks are left alone -- those are the
     * user asking for a new tab -- and every failure still navigates, after saying why.
     */
    private chainClick = async (e: any, fileUpload: FileUpload, slug: string, rank: number) => {
        if (e?.metaKey || e?.ctrlKey || e?.shiftKey || e?.button === 1) return;
        e.preventDefault();
        if (this.chaining) return;
        this.chaining = slug;
        this.chainNotice = null;
        this.forceUpdate();

        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CLICKED, { toSlug: slug, rank });
        } catch { /* never load-bearing */ }

        let downloadUrl: string | null = null;
        try {
            const output = fileUpload?.result?.output;
            // Multi-output results have no single file to carry; the chip still navigates.
            if (output && !Array.isArray(output)) {
                downloadUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);
            }
        } catch { /* fall through to an uncarried navigation */ }

        const result = await chainToTool(fileUpload, slug, downloadUrl);
        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CHAINED, {
                toSlug: slug, carried: result.carried, reason: result.reason || '',
            });
        } catch { /* never load-bearing */ }

        if (!result.carried) {
            this.chaining = null;
            this.chainNotice = result.reason === 'too-large'
                ? 'Too large to carry over — pick it again on the next page.'
                : 'Couldn\'t bring the file across — pick it again on the next page.';
            this.forceUpdate();
            await new Promise(r => setTimeout(r, 1800));
        }
        window.location.assign(result.href);
    };

    /** Last seen progress %, kept so the bar survives interleaved stage-text messages. */
    private latchedProgress: { pct: number, stage: string } | null = null;

    render() {
        const fu = this.props.fileUpload;
        const ws = fu.ws;
        // Latch progress across interleaved notification messages: the backend alternates
        // percentage and stage-text updates, and unmounting the bar on every text message
        // made the card collapse/expand constantly (owner UX report). The bar mounts on the
        // first percentage and stays until a terminal state, showing the last known value
        // (monotonic within a stage so multi-pass encodes don't run the bar backwards).
        const inFlight = fu.status !== this.FILEUPLOAD_STATE_FAILED
            && (!ws || ws.processStageSubState !== ProcessStageSubStateTypes.FAILED)
            && !(fu.result && fu.result.output)
            && (!ws || ws.processStage !== 'completed');
        if (ws && ((ws.processStage === 'processing' && ws.dataType === 'percentage')
            || (ws.processStage === 'upload' && ws.dataType === 'notification'))) {
            const v = Number(this.decodeData(fu));
            if (!isNaN(v)) {
                if (!this.latchedProgress || this.latchedProgress.stage !== ws.processStage) {
                    this.latchedProgress = { pct: v, stage: ws.processStage };
                } else {
                    this.latchedProgress.pct = Math.max(this.latchedProgress.pct, v);
                }
            }
        }
        const shouldShowProgressbar = inFlight && this.latchedProgress != null;
        return <div className={`id${this.props.fileUpload.id} ${styles['card']}`}>
            <div className={styles['card-body']} data-jkey={`file-list-item-card`}>

                <div className={`${styles['card-title']}`} data-jkey={`file-list-item-title`}>
                    {this.getOutputFileNameIfAvailable(this.props.fileUpload)}
                </div>
                <div className={styles['card-content']} >
                    {/* ONE always-mounted status row while the card is not failed. The old
                        three mutually-exclusive blocks swapped in and out per websocket
                        message (loader + text vs text vs nothing), which read as the card
                        collapsing and expanding. Same text-selection precedence as before:
                        local readableMessage outside processing, else the decoded ws
                        message, else the pre-ws placeholder. */}
                    {
                        this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED
                        && (!ws || ws.processStageSubState !== ProcessStageSubStateTypes.FAILED)
                        // A recovered/completed card with no live ws has nothing to say —
                        // matches the old first block, which hid once a result existed.
                        && ((ws && ws.processStage) || !fu.result)
                        && <div className={styles['status-line']} data-jkey="file-list-item-status">
                            {
                                inFlight
                                && <div className={styles['status-loader']}>
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            <span>
                                {
                                    fu.readableMessage
                                        && (!ws || (ws.processStage !== 'processing' && ws.dataType !== 'percentage'))
                                        ? fu.readableMessage
                                        : ws && ws.processStage
                                            ? this.decodeDataType(fu, ws.dataType, ws.data)
                                            : fu.readableMessage || 'Process starting...'
                                }
                            </span>
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType !== 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.props.fileUpload.ws.data || 'Conversion failed.'}
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType === 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.decodeDataType(this.props.fileUpload, 'enforcement_error', this.props.fileUpload.ws.data)}
                            {(() => { try { const d = JSON.parse(this.props.fileUpload.ws.data); return d.upgradeRequired ? <span> — <a href="/pricing" style={{color: '#007bff', textDecoration: 'underline'}}>Upgrade your plan</a></span> : <span> — Try again later.</span>; } catch { return null; } })()}
                        </div>
                    }

                    {
                        this.props.fileUpload.status === this.FILEUPLOAD_STATE_FAILED
                        && <div>
                            Upload failed...
                        </div>
                    }

                    {/* Which settings THIS run used — repeated runs of one file were
                        indistinguishable without it (sweep P2 UX). Silent on defaults. */}
                    {(() => {
                        const echo = formatOptionEcho(this.props.fileUpload.options as any);
                        return echo ? <div className={styles['option-echo']}>{echo}</div> : null;
                    })()}

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && <div>
                            {this.getProcessInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && this.isSingleFile(this.props.fileUpload.result.sizes.outputs)
                        && <div>
                            {this.getOutputInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.hasZipFileInfo
                        && <div>
                            {this.hasZipFileData}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.processingNotes
                        && this.props.fileUpload.result.processingNotes.length > 0
                        && <div className={styles['processing-notes']}>
                            {/* Every note renders. The old single-line + hover-tooltip form hid all
                                but the first note — including the best-effort disclosure ("Reaching
                                X would destroy this GIF…"), the one line the pre-convert copy
                                explicitly promises ("we'll tell you if that happens"). A tooltip is
                                not telling: mobile has no hover and desktop users don't know to. */}
                            {this.props.fileUpload.result.processingNotes.map((note, i) => (
                                <div key={i} className={styles['processing-notes-line']}>ⓘ {note}</div>
                            ))}
                        </div>
                    }

                    {

                        shouldShowProgressbar && <ProgressComponent
                            value={this.latchedProgress ? this.latchedProgress.pct : 0}
                            postFix='%'
                        >
                        </ProgressComponent>
                    }
                </div>
                {
                    !this.props.hideFooterButtons && this.getFooterButtons(this.props.fileUpload)
                }

            </div>
            {!this.hasFailed(this.props.fileUpload) &&
            <div className={styles['social-share-promote']}>
                {this.getShareCTA()}
                <div className={styles['social-share']}>
                    <div className={`${styles['share-btn']} ${styles['share-btn-primary']}`}
                        onClick={() => this.copyLink()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/></svg>
                        {this.state.linkCopied ? 'Copied!' : 'Copy Link'}
                    </div>
                    <div className={`${styles['share-btn']} ${styles['share-btn-credit']}`}
                        onClick={() => this.toggleCredit()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M10.478 1.647a.5.5 0 1 0-.956-.294l-4 13a.5.5 0 0 0 .956.294l4-13zM4.854 4.146a.5.5 0 0 1 0 .708L1.707 8l3.147 3.146a.5.5 0 0 1-.708.708l-3.5-3.5a.5.5 0 0 1 0-.708l3.5-3.5a.5.5 0 0 1 .708 0zm6.292 0a.5.5 0 0 0 0 .708L14.293 8l-3.147 3.146a.5.5 0 0 0 .708.708l3.5-3.5a.5.5 0 0 0 0-.708l-3.5-3.5a.5.5 0 0 0-.708 0z"/></svg>
                        Add us to your site
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnWhatsApp()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnReddit()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF4500" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595zM12 9.8c-3.446 0-6.24 1.791-6.24 4 0 2.21 2.794 4 6.24 4 3.447 0 6.241-1.79 6.241-4 0-2.209-2.794-4-6.241-4zm-3.44 1.4c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.663 0-1.2-.538-1.2-1.2 0-.662.537-1.2 1.2-1.2zm6.88 0c.663 0 1.2.538 1.2 1.2 0 .662-.537 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-5.26 3.843c.155-.156.408-.156.563 0 .586.585 1.679.635 2.257.635.578 0 1.671-.05 2.256-.635a.398.398 0 0 1 .563 0 .398.398 0 0 1 0 .563c-.789.789-2.214.851-2.819.851-.605 0-2.03-.062-2.82-.851a.398.398 0 0 1 0-.563z"/></svg>
                        Reddit
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.openTwitter()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.738 2h1.994L9.377 7.083 14.5 14h-4.011L7.346 9.805 3.751 14H1.757l4.658-5.437L1.5 2h4.113l2.84 3.834L11.738 2Zm-.7 10.781h1.105l-7.13-9.627H3.828l7.21 9.627Z" fill="#161616"/></svg>
                    </div>
                </div>
                {this.state.creditOpen && this.getCreditPanel()}
            </div>}
        </div>
    }


    getShareCTA(): JSX.Element {
        const fu = this.props.fileUpload;
        const saved = this.getSavedBytes();
        const ranClean = !(fu.result?.processingNotes?.length > 0);
        // Compress: lead with the number only when it's actually impressive —
        // a real percentage AND enough absolute bytes that it isn't comic
        // ("we saved you 1.03 KB").
        if (saved != null && saved.reduction > 5 && saved.bytes > 100 * 1024) {
            return <span>💚 We just saved you <b>{toReadableBytes(saved.bytes, 'quantity')}</b>, free. Help the next person find us.</span>;
        }
        if (ranClean) {
            switch (fu.processType) {
                case ProcessTypes.CONVERT:
                    return <span>💚 Converted free — no watermark, no signup. Help the next person find us.</span>;
                case ProcessTypes.MERGE:
                    return <span>💚 Merged into one file, free. Help others find us.</span>;
                case ProcessTypes.SPLIT:
                case ProcessTypes.EXTRACT:
                    return <span>💚 Done in seconds, free. Know someone who needs this?</span>;
                case ProcessTypes.RESIZE:
                case ProcessTypes.CROP:
                    return <span>💚 Resized free, no watermark. Help others find us.</span>;
            }
        }
        return <span>💚 This tool is free because people share it. Pass it on.</span>;
    }

    /** Bytes saved for compress jobs; null when not applicable. */
    getSavedBytes(): { bytes: number, reduction: number } | null {
        const fu = this.props.fileUpload;
        if (fu.processType !== ProcessTypes.COMPRESS || !fu.result?.sizes?.outputs) return null;
        try {
            const key = Object.keys(fu.result.sizes.outputs)[0];
            const outputSize = parseFloat(fu.result.sizes.outputs[key]);
            const reduction = Math.round(100 * (1 - outputSize / fu.size));
            if (!isNaN(reduction) && reduction > 0) return { bytes: fu.size - outputSize, reduction };
        } catch { }
        return null;
    }

    trackShare(channel: string) {
        try {
            this.metricsService.emit(MetricEventName.SHARE_CLICKED, {
                channel,
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory.BUTTON,
                GAContants.EventAction['SHARE-CLICK'],
                GAContants.EventLabel.NAVIGATE,
                `${channel}:${this.props.fileUpload.pageData?.uniqueProcessType || 'unknown'}`,
            );
        } catch { }
    }

    toggleCredit() {
        if (!this.state.creditOpen) {
            this.trackShare('credit-open');
            this.metricsService.emit(MetricEventName.CREDIT_PANEL_OPENED, {
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
        }
        this.setState({ creditOpen: !this.state.creditOpen, creditCopied: false });
    }

    /** A few anchor phrasings so thousands of pasted credits don't share one
        identical footprint. Stable per file (keyed on name length) but varied
        across users. */
    getCreditSnippet(format: 'html' | 'markdown' | 'plain'): string {
        const fu = this.props.fileUpload;
        const tool = fu.pageData?.uniqueProcessType || '';
        const url = `https://www.xconvert.com/${tool}`;
        // Derive the display name from the SLUG, never the SEO title — titles
        // carry marketing tails ("Compress PNG Free — No Limits…") that make
        // terrible anchor text.
        const small = ['to', 'a', 'an', 'the', 'into', 'and'];
        const toolName = tool
            ? tool.split('-').map(w => small.includes(w) ? w
                : w.length <= 4 ? w.toUpperCase()
                    : w[0].toUpperCase() + w.slice(1)).join(' ')
                .replace(/^Convert /, '')
            : 'XConvert';
        const saved = this.getSavedBytes();
        const verb = fu.processType === ProcessTypes.MERGE ? 'Merged my files with '
            : fu.processType === ProcessTypes.SPLIT || fu.processType === ProcessTypes.EXTRACT ? 'Split my file with '
                : fu.processType === ProcessTypes.RESIZE || fu.processType === ProcessTypes.CROP ? 'Resized my image with '
                    : fu.processType === ProcessTypes.COMPRESS ? 'Compressed my file with '
                        : 'Converted my file with ';
        const stats = (saved && saved.reduction > 5 && saved.bytes > 100 * 1024)
            ? `Compressed ${toReadableBytes(fu.size, 'quantity')} → ${toReadableBytes(fu.size - saved.bytes, 'quantity')} (−${saved.reduction}%) with `
            : verb;
        const anchors = [
            `the free ${toolName} tool on XConvert`,
            `XConvert's free ${toolName} tool`,
            `this free online ${toolName} tool`,
            `${toolName} at xconvert.com`,
        ];
        const anchor = anchors[(fu.name || '').length % anchors.length];
        if (format === 'html') return `${stats}<a href="${url}">${anchor}</a>.`;
        if (format === 'markdown') return `${stats}[${anchor}](${url}).`;
        return `${stats}${anchor}: ${url}`;
    }

    copyCredit() {
        navigator.clipboard.writeText(this.getCreditSnippet(this.state.creditFormat)).then(() => {
            this.trackShare(`credit-copy-${this.state.creditFormat}`);
            this.metricsService.emit(MetricEventName.CREDIT_COPIED, {
                format: this.state.creditFormat,
                tool: this.props.fileUpload.pageData?.uniqueProcessType || 'unknown',
            });
            this.setState({ creditCopied: true });
            setTimeout(() => this.setState({ creditCopied: false }), 2000);
        });
    }

    getCreditPanel(): JSX.Element {
        const formats: Array<'html' | 'markdown' | 'plain'> = ['html', 'markdown', 'plain'];
        const labels = { html: 'HTML', markdown: 'Markdown', plain: 'Plain' };
        return <div className={styles['credit-panel']}>
            <div className={styles['credit-panel-lead']}>
                Have a blog, class page, wiki, or README? One pasted line does more for
                us than anything else — it&apos;s how new people find a free tool with no ad budget.
            </div>
            <div className={styles['credit-tabs']}>
                {formats.map(f => (
                    <span key={f}
                        className={`${styles['credit-tab']} ${this.state.creditFormat === f ? styles['credit-tab-active'] : ''}`}
                        onClick={() => this.setState({ creditFormat: f, creditCopied: false })}>
                        {labels[f]}
                    </span>
                ))}
            </div>
            <div className={styles['credit-snippet-row']}>
                <code className={styles['credit-snippet']}>{this.getCreditSnippet(this.state.creditFormat)}</code>
                <div className={`${styles['share-btn']} ${styles['share-btn-primary']}`} onClick={() => this.copyCredit()}>
                    {this.state.creditCopied ? 'Copied!' : 'Copy'}
                </div>
            </div>
            <div className={styles['credit-panel-foot']}>
                No website? Sharing with one person helps too. 🙏 &nbsp;
                <a href={`/link-us?tool=${this.props.fileUpload.pageData?.uniqueProcessType || ''}`}>More ways to link us →</a>
            </div>
        </div>;
    }

    copyLink() {
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData?.uniqueProcessType || ''}`;
        navigator.clipboard.writeText(url).then(() => {
            this.trackShare('copy-link');
            this.setState({ linkCopied: true });
            setTimeout(() => this.setState({ linkCopied: false }), 2000);
        });
    }

    shareOnWhatsApp() {
        this.trackShare('whatsapp');
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(this.props.fileUpload.pageData.title + ' ' + url)}`, 'popupwindow', 'scrollbars=yes,width=700,height=400')?.focus();
    }

    shareOnReddit() {
        this.trackShare('reddit');
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`;
        window.open(
            `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(this.props.fileUpload.pageData.title)}`,
            'popupwindow',
            'scrollbars=yes,width=900,height=650',
        )?.focus();
    }

    openTwitter() {
        this.trackShare('x');
        var popUp = window.open(`https://twitter.com/intent/tweet?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}&text=${encodeURIComponent(this.props.fileUpload.pageData.title)}&via=xconvert_com&hashtags=xconvert`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }

}
